import { useRef, useState } from 'react'
import { formatCurrency } from '../projectionsFormat'

function parseMoneyInput(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed || trimmed === '-') return null
  const negative = trimmed.startsWith('(') && trimmed.endsWith(')')
  const cleaned = trimmed.replace(/[$,()]/g, '').trim()
  if (!cleaned) return null
  const parsed = Number(cleaned)
  if (!Number.isFinite(parsed)) return null
  return negative ? -parsed : parsed
}

function formatMoneyDisplay(value: number | null | undefined): string {
  if (value == null) return ''
  if (value === 0) return '$0.00'
  return formatCurrency(value)
}

type PolicyMoneyInputProps = {
  value: number | null | undefined
  disabled?: boolean
  className?: string
  onChange: (value: number | null) => void
}

export default function PolicyMoneyInput({
  value,
  disabled = false,
  className = '',
  onChange,
}: PolicyMoneyInputProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const editingRef = useRef(false)
  const draftRef = useRef('')

  const commit = () => {
    const parsed = parseMoneyInput(draftRef.current)
    onChange(parsed)
    editingRef.current = false
    setEditing(false)
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      disabled={disabled}
      className={className}
      value={editing ? draft : formatMoneyDisplay(value)}
      onFocus={() => {
        if (disabled) return
        const nextDraft = value == null ? '' : String(value)
        draftRef.current = nextDraft
        editingRef.current = true
        setDraft(nextDraft)
        setEditing(true)
      }}
      onChange={(e) => {
        const nextDraft = e.target.value
        draftRef.current = nextDraft
        editingRef.current = true
        setEditing(true)
        setDraft(nextDraft)
        if (nextDraft.trim() === '') {
          onChange(null)
          return
        }
        const parsed = parseMoneyInput(nextDraft)
        if (parsed !== null) {
          onChange(parsed)
        }
      }}
      onBlur={() => {
        if (editingRef.current) commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        }
      }}
    />
  )
}
